import React from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Select as AntSelect } from "antd";
import { UseFormReturn } from "react-hook-form";
import { PipelineFieldRow, PIPELINE_INPUT_CLASS } from "@/components/form/pipelineFieldRow";
import { usePipelineForm } from "@/components/form/PipelineFormContext";
import { cn } from "@/lib/utils";

interface SelectOption {
  readonly value: string;
  readonly label: string;
}

interface SelectFieldProps {
  form: UseFormReturn<any>;
  name: string;
  label: string;
  options: readonly SelectOption[];
  required?: boolean;
  placeholder?: string;
  className?: string;
  onChange?: (value: string) => void;
  variant?: 'default' | 'pipeline';
  showSearch?: boolean;
}

export function SelectField({
  form,
  name,
  label,
  options,
  required = false,
  placeholder = "Selecione uma opção",
  className = "",
  onChange,
  variant,
  showSearch = false,
}: SelectFieldProps) {
  const { variant: contextVariant } = usePipelineForm();
  const resolvedVariant = variant ?? contextVariant;
  const isPipeline = resolvedVariant === 'pipeline';

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const errorMessage = form.formState.errors[name]?.message as string | undefined;

        if (isPipeline) {
          return (
            <FormItem className={cn('flex flex-col p-0 space-y-0', className)}>
              <PipelineFieldRow label={label} required={required} error={errorMessage}>
                <AntSelect
                  value={field.value || undefined}
                  placeholder={placeholder}
                  onChange={(value) => {
                    field.onChange(value);
                    onChange?.(value);
                  }}
                  options={options.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  variant="borderless"
                  className={cn(PIPELINE_INPUT_CLASS, 'w-full')}
                  showSearch={showSearch}
                  optionFilterProp="label"
                  status={errorMessage ? 'error' : undefined}
                  allowClear
                />
              </PipelineFieldRow>
            </FormItem>
          );
        }

        return (
          <FormItem className={className}>
            <FormLabel>{label}{required && "*"}</FormLabel>
            <Select
              onValueChange={(value) => {
                field.onChange(value);
                onChange?.(value);
              }}
              value={field.value || ""}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
